<script setup lang="ts">
import Icon from '@/components/shared/Icon.vue'
import { CheckboxIndicator, CheckboxRoot } from 'radix-vue'
import { computed } from 'vue'

interface Props {
  modelValue?: boolean
  type?: 'indeterminate' | 'default'
}

const props = withDefaults(defineProps<Props>(), {
  type: 'default',
})

defineEmits<{
  'update:modelValue': [value: boolean]
}>()

const checked = computed(() => {
  if (props.type === 'indeterminate') {
    return 'indeterminate'
  }

  return props.modelValue
})
</script>

<template>
  <CheckboxRoot
    :checked="checked"
    v-bind="$attrs"
    class="peer size-[18px] shrink-0 rounded-[5px] relative isolate inline-flex items-center justify-center transition-all duration-300 ease-[cubic-bezier(0.4,0.36,0,1)] before:duration-300 before:ease-[cubic-bezier(0.4,0.36,0,1)] before:transition-opacity border border-gray-300 bg-white shadow-[0_1px_theme(colors.white/0.07)_inset,0_1px_3px_theme(colors.gray.900/0.1)] before:pointer-events-none before:absolute before:inset-0 before:-z-10 before:rounded-[5px] before:bg-gradient-to-b before:from-white/20 before:opacity-50 hover:before:opacity-100 after:pointer-events-none after:absolute after:inset-0 after:-z-10 after:rounded-[5px] after:bg-gradient-to-b after:from-white/10 after:from-[46%] after:to-[54%] after:mix-blend-overlay default-focus disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary-500 data-[state=checked]:border-primary-500/50 data-[state=checked]:text-white data-[state=indeterminate]:bg-primary-500 data-[state=indeterminate]:border-primary-500/50 data-[state=indeterminate]:text-white dark:border-gray-600 dark:bg-gray-800 dark:shadow-[0_1px_theme(colors.white/0.05)_inset,0_1px_3px_theme(colors.gray.950/0.3)] dark:data-[state=checked]:bg-primary-600 dark:data-[state=checked]:border-primary-600/50 dark:data-[state=checked]:hover:bg-primary-500 dark:data-[state=checked]:hover:border-primary-500/50 dark:data-[state=indeterminate]:bg-primary-600 dark:data-[state=indeterminate]:border-primary-600/50 dark:data-[state=indeterminate]:hover:bg-primary-500 dark:data-[state=indeterminate]:hover:border-primary-500/50"
    @update:checked="$emit('update:modelValue', $event)"
  >
    <CheckboxIndicator class="flex absolute inset-0 justify-center items-center text-current">
      <template v-if="type === 'indeterminate'">
        <Icon variant="minus" class="size-3.5" :stroke-width="2.5" />
      </template>
      <template v-else-if="type === 'default'">
        <Icon variant="check" class="size-3" :stroke-width="2.5" />
      </template>
    </CheckboxIndicator>
  </CheckboxRoot>
</template>
